import React, { useEffect } from "react";

import { useDebouncedCallback } from "@tanstack/react-pacer";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnFiltersState,
  type RowSelectionState,
  type Updater,
} from "@tanstack/react-table";

import type { LibreViewClinicPatient } from "@/models/LibreViewModel.ts";
import { AgeCell, AgeHeader } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/AgeColumn.tsx";
import {
  IgnoreCell,
  IgnoreHeader,
} from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/IgnoreColumn.tsx";
import {
  LastDataSinceCell,
  LastDataSinceHeader,
} from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/LastDataSinceColumn.tsx";
import { NameCell, NameHeader } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/NameColumn.tsx";
import {
  SelectCell,
  SelectHeader,
} from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/SelectColumn.tsx";
import { TBRCell, TBRHeader } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/TBRColumn.tsx";
import { TIRCell, TIRHeader } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/columns/TIRColumn.tsx";
import {
  DEFAULT_COLUMN_FILTERS,
  filterLogic,
  type FilterData,
  tableFilterFn,
} from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/filterFns.ts";

const columnHelper = createColumnHelper<LibreViewClinicPatient>();

const columnDefinitions = {
  select: columnHelper.display({
    id: "select",
    header: SelectHeader,
    cell: SelectCell,
    size: 10,
  }),
  name: columnHelper.accessor("lastName", {
    id: "name",
    cell: NameCell,
    header: NameHeader,
    size: 30,
  }),
  tir: columnHelper.accessor("tir", {
    cell: TIRCell,
    header: TIRHeader,
    size: 13,
  }),
  tbr: columnHelper.accessor("tbr", {
    cell: TBRCell,
    header: TBRHeader,
    size: 13,
  }),
  age: columnHelper.accessor("age", {
    cell: AgeCell,
    header: AgeHeader,
    size: 13,
  }),
  lastDataSince: columnHelper.accessor("lastDataSince", {
    cell: LastDataSinceCell,
    header: LastDataSinceHeader,
    size: 20,
  }),
  ignore: columnHelper.display({
    id: "ignore",
    cell: IgnoreCell,
    header: IgnoreHeader,
    size: 10,
  }),
  filterColumn: columnHelper.accessor(
    data => ({
      lastDataSince: data.lastDataSince,
      tir: data.tir,
      tbr: data.tbr,
    }),
    {
      id: "filterColumn",
      header: "Filter",
      enableHiding: true,
      size: 0,
      filterFn: tableFilterFn,
    },
  ),
};

export type UseLibreviewGroupedInclusionTableProps = {
  patients: LibreViewClinicPatient[];
  setRowSelection: (rowSelection: LibreViewClinicPatient[]) => void;
};

const updateRowSelectionStateFromFilters = (patients: LibreViewClinicPatient[], columnFilters: ColumnFiltersState) => {
  return patients.reduce<RowSelectionState>((acc, patient) => {
    const isDisplayed = filterLogic(
      patient,
      (columnFilters[0]?.value as FilterData | undefined) ?? DEFAULT_COLUMN_FILTERS,
    );
    return {
      ...acc,
      ...(isDisplayed ? { [patient.libreviewId]: true } : {}),
    };
  }, {});
};

export const useLibreviewGroupedInclusionTable = ({
  patients,
  setRowSelection,
}: UseLibreviewGroupedInclusionTableProps) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    { id: "filterColumn", value: DEFAULT_COLUMN_FILTERS },
  ]);
  const [rowSelectionState, setRowSelectionState] = React.useState<RowSelectionState>(
    updateRowSelectionStateFromFilters(patients, columnFilters),
  );

  const debouncedSetColumnFilters = useDebouncedCallback(
    (updater: Updater<ColumnFiltersState>) =>
      setColumnFilters(oldState => {
        const newState = typeof updater === "function" ? updater(oldState) : updater;
        setRowSelectionState(updateRowSelectionStateFromFilters(patients, newState));
        return newState;
      }),
    {
      wait: 500,
    },
  );

  // Send the new selection to the parent component when the selection state changes
  useEffect(() => {
    const selectedRowsSet = new Set(
      Object.entries(rowSelectionState)
        .filter(([, selected]) => selected)
        .map(([libreviewId]) => libreviewId),
    );
    const selectedPatients = patients.filter(p => selectedRowsSet.has(p.libreviewId));
    setRowSelection(selectedPatients);
  }, [rowSelectionState, patients, setRowSelection]);

  return useReactTable<LibreViewClinicPatient>({
    columns: Object.values(columnDefinitions),
    data: patients,
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: 100,
    },
    getRowId: row => row.libreviewId,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    state: {
      rowSelection: rowSelectionState,
      columnFilters,
      columnVisibility: {
        filterColumn: false,
      },
    },
    onRowSelectionChange: setRowSelectionState,
    onColumnFiltersChange: debouncedSetColumnFilters,
  });
};
